import type { ColumnProfileResponse } from "../types";
import { escapeHtml } from "../utils/texto";
import { icon } from "../utils/icons";

interface PickerOptions {
  id: string;
  label: string;
  profiles: ColumnProfileResponse[];
  selectedId: string | null;
  draft: boolean;
  onSelect: (id: string | null) => void;
}

/** Portal-backed picker: a source card clips its own surface, never its menu. */
export class ProfilePicker {
  private _trigger: HTMLButtonElement;
  private _panel: HTMLElement | null = null;
  private _controller: AbortController | null = null;
  private _observer: MutationObserver | null = null;
  private _filtered: ColumnProfileResponse[] = [];
  private _active = -1;

  constructor(private _host: HTMLElement, private _opts: PickerOptions) {
    const selected = _opts.profiles.find(p => p.id === _opts.selectedId);
    const name = selected?.nombre ?? (_opts.draft ? "Mapeo sin guardar (uso único)" : "— Elija un perfil —");
    _host.innerHTML = `<button class="profile-picker__trigger" type="button" id="${_opts.id}" aria-label="${escapeHtml(_opts.label)}: ${escapeHtml(name)}" aria-haspopup="listbox" aria-expanded="false" aria-controls="${_opts.id}-list" ${_opts.profiles.length < 6 ? 'role="combobox"' : ''}>
      <span class="profile-picker__value">${escapeHtml(name)}</span><span class="profile-picker__chevron" aria-hidden="true">${icon("chevron")}</span></button>`;
    this._trigger = _host.querySelector<HTMLButtonElement>("button")!;
    this._trigger.addEventListener("click", () => this._panel ? this.close() : this.open());
    this._trigger.addEventListener("keydown", e => this._keydown(e));
  }

  open(last = false): void {
    if (this._panel) return;
    this._controller = new AbortController();
    const signal = this._controller.signal;
    this._panel = document.createElement("div");
    this._panel.className = "profile-picker__panel";
    const search = this._opts.profiles.length >= 6;
    this._panel.innerHTML = `${search ? `<input class="profile-picker__search" type="search" role="combobox" aria-label="Buscar perfiles de ${escapeHtml(this._opts.label)}" aria-autocomplete="list" aria-expanded="true" aria-controls="${this._opts.id}-list" placeholder="Buscar perfil…" autocomplete="off" />` : ''}
      <div class="profile-picker__list" id="${this._opts.id}-list" role="listbox" aria-label="${escapeHtml(this._opts.label)}"></div>
      <p class="profile-picker__empty" role="status" hidden>${this._opts.profiles.length ? 'No hay perfiles que coincidan.' : 'Todavía no hay perfiles guardados.'}</p>`;
    document.body.appendChild(this._panel);
    this._trigger.setAttribute("aria-expanded", "true");
    this._filtered = this._opts.profiles;
    const selected = this._filtered.findIndex(p => p.id === this._opts.selectedId);
    this._active = selected >= 0 ? selected : last ? this._filtered.length - 1 : 0;
    this._renderOptions();
    this._position();
    const input = this._panel.querySelector<HTMLInputElement>("input");
    input?.addEventListener("input", () => {
      const query = input.value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
      this._filtered = this._opts.profiles.filter(p => `${p.nombre} ${p.banco_o_erp ?? ''}`.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase().includes(query));
      this._active = this._filtered.length ? 0 : -1;
      this._renderOptions();
    }, { signal });
    input?.addEventListener("keydown", e => this._keydown(e), { signal });
    this._panel.addEventListener("keydown", e => {
      if (e.key === "Escape" && e.target !== input) { e.preventDefault(); this.close(true); }
    }, { signal });
    this._panel.addEventListener("click", e => {
      const option = (e.target as Element).closest<HTMLElement>(".profile-picker__option");
      if (option) this._select(this._filtered[Number(option.dataset.index)]?.id ?? null);
    }, { signal });
    // Keep combobox focus while clicking an option; Tab is never trapped.
    this._panel.addEventListener("mousedown", e => {
      if ((e.target as Element).closest(".profile-picker__option")) e.preventDefault();
    }, { signal });
    document.addEventListener("pointerdown", e => {
      if (!this._host.contains(e.target as Node) && !this._panel?.contains(e.target as Node)) this.close();
    }, { signal });
    document.addEventListener("focusin", e => {
      if (!this._host.contains(e.target as Node) && !this._panel?.contains(e.target as Node)) this.close();
    }, { signal });
    window.addEventListener("resize", () => this._position(), { signal });
    window.addEventListener("scroll", () => this._position(), { signal, capture: true });
    this._observer = new MutationObserver(() => { if (!this._host.isConnected) this.close(); });
    this._observer.observe(document.body, { childList: true, subtree: true });
    input?.focus();
  }

  close(focus = false): void {
    this._controller?.abort();
    this._controller = null;
    this._observer?.disconnect();
    this._observer = null;
    this._panel?.remove();
    this._panel = null;
    this._trigger.setAttribute("aria-expanded", "false");
    this._trigger.removeAttribute("aria-activedescendant");
    if (focus && this._host.isConnected) this._trigger.focus();
  }

  destroy(): void { this.close(); }

  private _select(id: string | null): void {
    // Empty/filter results never choose another profile as a fallback.
    if (!id || !this._opts.profiles.some(p => p.id === id)) return;
    this.close();
    this._opts.onSelect(id);
  }

  private _keydown(e: KeyboardEvent): void {
    if (e.key === "Escape") { if (this._panel) { e.preventDefault(); this.close(true); } return; }
    if (e.key === "Tab") {
      this.close(e.target !== this._trigger); return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!this._panel) { this.open(e.key === "ArrowUp"); return; }
      if (this._filtered.length) {
        this._active = (this._active + (e.key === "ArrowDown" ? 1 : -1) + this._filtered.length) % this._filtered.length;
        this._renderOptions();
      }
    } else if (this._panel && (e.key === "Home" || e.key === "End") && e.target === this._trigger) {
      e.preventDefault(); this._active = e.key === "Home" ? 0 : this._filtered.length - 1; this._renderOptions();
    } else if (e.key === "Enter" && this._panel) {
      e.preventDefault(); this._select(this._filtered[this._active]?.id ?? null);
    } else if (e.key === " " && e.target === this._trigger && this._panel) {
      e.preventDefault(); this._select(this._filtered[this._active]?.id ?? null);
    }
  }

  private _renderOptions(): void {
    if (!this._panel) return;
    const list = this._panel.querySelector<HTMLElement>(".profile-picker__list")!;
    list.innerHTML = this._filtered.map((p, i) => {
      const secondary = p.banco_o_erp && p.banco_o_erp.trim().toLocaleLowerCase() !== p.nombre.trim().toLocaleLowerCase() ? p.banco_o_erp : '';
      return `<div class="profile-picker__option${i === this._active ? ' is-active' : ''}" role="option" id="${this._opts.id}-option-${i}" data-index="${i}" aria-selected="${p.id === this._opts.selectedId}">
        <span><strong>${escapeHtml(p.nombre)}</strong>${secondary ? `<small>${escapeHtml(secondary)}</small>` : ''}</span>${p.id === this._opts.selectedId ? icon("check") : ''}</div>`;
    }).join("");
    this._panel.querySelector<HTMLElement>(".profile-picker__empty")!.hidden = this._filtered.length > 0;
    const combobox = this._panel.querySelector<HTMLInputElement>("input") ?? this._trigger;
    if (this._filtered[this._active]) {
      combobox.setAttribute("aria-activedescendant", `${this._opts.id}-option-${this._active}`);
      const active = list.querySelector<HTMLElement>(".is-active");
      if (active) {
        if (active.offsetTop < list.scrollTop) list.scrollTop = active.offsetTop;
        else if (active.offsetTop + active.offsetHeight > list.scrollTop + list.clientHeight) {
          list.scrollTop = active.offsetTop + active.offsetHeight - list.clientHeight;
        }
      }
    } else combobox.removeAttribute("aria-activedescendant");
  }

  private _position(): void {
    if (!this._panel) return;
    const rect = this._host.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) { this.close(); return; }
    const margin = 12;
    const below = window.innerHeight - rect.bottom - margin - 6;
    const above = rect.top - margin - 6;
    const upward = below < 180 && above > below;
    const available = Math.max(48, upward ? above : below);
    const width = Math.min(Math.max(rect.width, 280), window.innerWidth - margin * 2);
    this._panel.style.width = `${width}px`;
    this._panel.style.maxHeight = `${Math.min(340, available)}px`;
    this._panel.style.left = `${Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin))}px`;
    this._panel.style.top = `${Math.max(margin, upward ? rect.top - this._panel.getBoundingClientRect().height - 6 : rect.bottom + 6)}px`;
  }
}
